import {
  cellPaddingStyle,
  cssClass,
  elideTextStyle,
  linkClass,
  SiteUrlsContext,
  TextCellFormatter,
  TypedBodyCellProps
} from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import * as React from 'react';
import { Link } from 'react-router-dom';

export const linkCellClass = cssClass('linkCell', elideTextStyle, cellPaddingStyle, { display: 'flex' });
export const noPaddingLinkCellClass = cssClass('noPaddingLinkCell', elideTextStyle, { display: 'flex' });
const linkContainerClass = cssClass('linkContainerClass', {
  textOverflow: 'ellipsis',
  overflow: 'hidden'
});

const disabledLinkContainerClass = cssClass('linkContainerClass', {
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  pointerEvents: 'none',
  opacity: 0.6,
});

const postIconClass = cssClass('postIconClass', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingLeft: '8px',
});

export interface LinkCellFormatterProps {
  readonly text: string | null;
  readonly pathname: string;
  readonly noPadding: boolean;
  readonly targetBlank?: boolean;
  readonly disableTabIndex?: boolean;
  readonly useAnchor?: boolean;  // When true the LinkCellFormatter renders a native anchor tag instead of a ReactRouter Link component
  readonly postIcon?: JSX.Element | null; // An icon that appears after the link text.
  readonly linkTitle?: string | null; // Title displayed for just the text link
  readonly disabled?: boolean; // Flag to control when to add the disabled style
}

export function LinkCellFormatter({
  text,
  pathname,
  noPadding,
  targetBlank = false,
  useAnchor = false,
  postIcon = null,
  linkTitle = null,
  disableTabIndex = false,
  disabled = false,
}: LinkCellFormatterProps) {
  return (
    <div className={noPadding ? noPaddingLinkCellClass : linkCellClass}>
      <div className={disabled ? disabledLinkContainerClass : linkContainerClass}>
        {
          useAnchor
            ? anchorContents({ text, pathname, noPadding, targetBlank, postIcon, linkTitle, disableTabIndex })
            : linkContents({ text, pathname, noPadding, targetBlank, postIcon, linkTitle, disableTabIndex })
        }
      </div>
      {
        postIcon
          ? <div className={postIconClass}>{postIcon}</div>
          : null
      }
    </div>
  );
}

function anchorContents({
  text,
  pathname,
  targetBlank,
  linkTitle,
  disableTabIndex,
}: LinkCellFormatterProps): JSX.Element {
  return (
    <a
      className={linkClass}
      href={pathname}
      title={linkTitle ?? text ?? undefined}
      target={targetBlank ? '_blank' : undefined}
      tabIndex={disableTabIndex ? -1 : 0} rel="noreferrer"
    >
      {text}
    </a>
  );
}

type cellClickingTelemetryContextType = (telemetryHandler: (onClick: () => void) => React.ReactElement) => React.ReactElement;
export const cellClickingTelemetryContext = React.createContext<cellClickingTelemetryContextType>(telemetryHandler =>
  telemetryHandler(() => <></>));


export function linkContents({
  text,
  pathname,
  targetBlank,
  linkTitle,
  disableTabIndex,
}: LinkCellFormatterProps): JSX.Element {
  return (
    <cellClickingTelemetryContext.Consumer>
      {
        telemetryHandler => telemetryHandler(onMouseUp =>
          <Link
            onMouseUp={onMouseUp}
            className={linkClass}
            to={pathname}
            title={linkTitle ?? text ?? undefined}
            target={targetBlank ? '_blank' : undefined}
            tabIndex={disableTabIndex ? -1 : 0}
          >
            {text}
          </Link>
        )}
    </cellClickingTelemetryContext.Consumer>
  );
}

export function createLinkCellFormatter<T>(
  getTextFromRowData: (rowData: T) => string | null,
  getPathFromRowData: (urls: SiteUrls, rowData: T) => string,
  noPadding = false,
  useAnchor = false,
  disableTabIndex = false,
  getIconFromRowData: (rowdata: T) => JSX.Element | null = () => null,
  getLinkTitleFromRowData: (rowData: T) => string | null = () => null,
  getDisabledFromRowData?: (rowData: T) => boolean,
): React.Factory<TypedBodyCellProps<T>> {
  return (props: TypedBodyCellProps<T>) => (
    <SiteUrlsContext.Consumer>
      {urls =>
        <LinkCellFormatter
          text={getTextFromRowData(props.row.data)}
          pathname={getPathFromRowData(urls, props.row.data)}
          noPadding={noPadding}
          useAnchor={useAnchor}
          postIcon={getIconFromRowData(props.row.data)}
          linkTitle={getLinkTitleFromRowData(props.row.data)}
          disableTabIndex={disableTabIndex}
          disabled={getDisabledFromRowData ? getDisabledFromRowData(props.row.data) : false}
        />
      }
    </SiteUrlsContext.Consumer>
  );
}

export function createLinkOrTextCellFormatter<T>(
  getTextFromRowData: (rowData: T) => string | null,
  getPathFromRowData: (urls: SiteUrls, rowData: T) => string | null,
  noPadding = false,
  targetBlank = false,
  useAnchor = false
): React.Factory<TypedBodyCellProps<T>> {
  return (props: TypedBodyCellProps<T>) => (
    <SiteUrlsContext.Consumer>
      {urls => {
        const text = getTextFromRowData(props.row.data);
        const url = getPathFromRowData(urls, props.row.data);
        if (url) {
          return <LinkCellFormatter text={text} pathname={url} noPadding={noPadding} targetBlank={targetBlank} useAnchor={useAnchor} />;
        } else {
          return <TextCellFormatter text={text} noPadding={noPadding} />;
        }
      }}
    </SiteUrlsContext.Consumer>
  );
}
